import React, { useState, useEffect, useRef } from 'react';
import { 
    Check, Rocket, Play, ArrowRight, AlertTriangle, ExternalLink, Plus,
    CheckCircle, X
} from 'lucide-react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';

// Shared UI
import { 
    AppButton, AppInput, AppCard, BlackboxLoaderIcon 
} from '../../components/SharedUI';

// --- SUB-COMPONENTS ---

const NavLink = ({ target, children, navigate }) => (
    <a href={`#${target}`} onClick={(e) => { e.preventDefault(); navigate(target); }} className="hover:text-brand-purple transition-colors">{children}</a>
);

const SignInModal = ({ isOpen, onClose, onSignIn }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        await onSignIn(email, password);
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <AppCard className="p-8 max-w-md w-full">
                <h3 className="text-xl font-bold font-poppins text-white text-center mb-6">Sign In</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <AppInput type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
                    <AppInput type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required />
                    <AppButton type="submit" className="w-full" size="lg" disabled={loading}>
                        {loading ? 'Signing In...' : 'Sign In'}
                    </AppButton>
                </form>
                <div className="text-center mt-4">
                    <AppButton variant="ghost" onClick={onClose}>Cancel</AppButton>
                </div>
            </AppCard>
        </div>
    );
};

const SignUpForm = ({ onSignUp }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }

        setLoading(true);
        try {
            await onSignUp(email, password);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 text-left">
            <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Email Address</label>
                <AppInput 
                    type="email" 
                    placeholder="name@example.com" 
                    required 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                />
            </div>
            
            <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Password</label>
                <AppInput 
                    type="password" 
                    placeholder="Create a secure password" 
                    required 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-zinc-400 mb-1">Confirm Password</label>
                <AppInput 
                    type="password" 
                    placeholder="Re-enter your password" 
                    required 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                />
            </div>

            {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2">
                    <AlertTriangle size={16} />
                    {error}
                </div>
            )}

            <div className="bg-zinc-900 border-2 border-dashed border-zinc-700 p-4 rounded-lg text-zinc-500 text-center mt-6">
                <p className="font-semibold text-sm text-zinc-400">Secure Payment Gateway</p>
                <p className="text-xs mt-1">Payments are processed securely by Stripe. No credit card information is stored on our servers.</p>
            </div>

            <button 
                type="submit" 
                disabled={loading} 
                className="w-full bg-brand-purple hover:bg-purple-500 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 text-lg glow-effect mt-4"
            >
                {loading ? 'Processing...' : 'Complete Sign Up'}
            </button>
            <p className="text-xs text-zinc-500 text-center mt-4">
                By signing up, you agree to our{' '}
                <button type="button" onClick={() => window.open('/terms', '_blank')} className="text-zinc-400 hover:text-white underline">
                    Terms of Service
                </button>
                {' '}and{' '}
                <button type="button" onClick={() => window.open('/privacy', '_blank')} className="text-zinc-400 hover:text-white underline">
                    Privacy Policy
                </button>.
            </p>
        </form>
    );
};

const NewFeaturesSection = () => {
    const [modalData, setModalData] = useState(null);
    const [activeSection, setActiveSection] = useState('phase-1');

    const sectionsRef = useRef([]);

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.4 
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        }, observerOptions);

        const currentSections = sectionsRef.current;
        currentSections.forEach(section => {
            if (section) observer.observe(section);
        });

        return () => {
            currentSections.forEach(section => {
                if (section) observer.unobserve(section);
            });
        };
    }, []);

    const featureData = {
        audience: {
            title: "1. Audience Genius",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>The heart of the platform. Upload your film's title, logline, genre, cast, and key themes. Our AI performs a deep analysis to generate a comprehensive profile of your ideal viewer.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Stop wasting your marketing budget. This tool delivers a multi-page report detailing:</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li><strong>Primary & Secondary Audience Profiles:</strong> Precise demographic breakdowns (age, gender, location, affinity).</li>
                    <li><strong>Psychographics & Interests:</strong> Understand what your audience reads, watches, and values.</li>
                    <li><strong>Top Theatrical Markets:</strong> Pinpoint the top 20 U.S. cities where your film will perform best.</li>
                    <li><strong>Strategic Festival Slate:</strong> A curated list of Tier 1, 2, and 3 festivals perfect for your film's premiere and sales strategy.</li>
                    <li><strong>Ad Platform Blueprints:</strong> Get exact targeting parameters for Meta (Facebook/Instagram) and X (Twitter), including interests, keywords, and follower lookalikes.</li>
                </ul>
            `
        },
        intelligence: {
            title: "2. Campaign Intelligence Center",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>Go beyond the logline. Upload your script or treatment for an in-depth marketing analysis of the narrative itself.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Find the marketing gold *inside* your story before you even shoot.</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                   <li><strong>Marketable Moments:</strong> Identify the key scenes with the highest potential for trailers and social clips.</li>
                   <li><strong>Quotable Dialogue:</strong> Extract the lines that will become your ad copy and social media captions.</li>
                   <li><strong>Optimal Release Strategy:</strong> Get an AI-driven recommendation for the best release window, identifying periods to avoid and strategic timing opportunities.</li>
                   <li><strong>Production Marketing Guide:</strong> Generate a list of behind-the-scenes moments to capture during production that will directly support your future marketing campaigns.</li>
                </ul>
            `
        },
        revenue: {
            title: "3. Revenue Forecaster",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>A powerful financial modeling tool that projects your film's potential earnings based on its unique data, comparable titles, and your strategic inputs.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Pitch investors and negotiate distribution deals from a position of strength.</p>
                 <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li><strong>Theatrical & Streaming Models:</strong> Generate separate forecasts for traditional theatrical releases or streaming-only strategies.</li>
                    <li><strong>Detailed Projections:</strong> Get breakdowns for opening weekend, domestic/international box office, and ancillary revenue streams (Home Entertainment, SVOD, etc.).</li>
                    <li><strong>Data-Backed Confidence:</strong> Walk into any meeting with a clear, defensible financial forecast that justifies your budget and vision.</li>
                 </ul>
            `
        },
        budget: {
            title: "4. Budget Planner",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>Translates your total marketing budget into a detailed, AI-optimized allocation plan.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Allocate every dollar for maximum impact. The AI considers your specific genre, audience, and release strategy to recommend the most effective spending mix across digital ads, PR, festivals, and more.</p>
            `
        },
        competitor: {
            title: "5. Competitive Intelligence",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>Enter the title of any competitor film, and the AI will generate a deep-dive analysis of its marketing strategy.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Learn from others' successes and failures. Understand their market positioning, identify their audience overlaps, and discover strategic opportunities and threats for your own campaign.</p>
            `
        },
        visual: {
            title: "6. Visual A/B Test Lab",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>Upload two versions of your movie poster. Our AI analyzes both based on your film's genre and themes, recommending the version with the highest market appeal.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Make data-driven creative decisions. Get a clear recommendation, a confidence score, and a detailed breakdown of why one visual is more effective, ensuring your key art connects with your target audience.</p>
            `
        },
        adstudio: {
            title: "7. Ad Concept Studio",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>The ultimate cure for creative block. Based on your Audience Genius report, the AI generates three distinct A/B test campaign concepts.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Instantly generate a slate of professional ad concepts, each complete with:</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                   <li><strong>Target Segment & Hypothesis:</strong> A clear strategic goal for each test.</li>
                   <li><strong>Ad A & Ad B:</strong> Two distinct ads with unique headlines and body copy.</li>
                   <li><strong>AI-Generated Visuals:</strong> Each ad concept comes with a unique, AI-generated image based on a detailed creative prompt, ready to be used or refined.</li>
                </ul>
            `
        },
        remix: {
            title: "8. Audience Voice Remix Studio",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>Turn real audience reactions into powerful marketing content. Upload screenshots of positive social media comments.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Leverage authentic social proof to build trust and drive engagement.</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                   <li><strong>AI Curation Bin:</strong> The AI automatically extracts the text, sentiment, and key tags from each comment.</li>
                   <li><strong>AI Graphic Generator:</strong> Transform a single powerful quote into three distinct, visually stunning social media posts (for Instagram, X, Facebook) complete with AI-generated backgrounds.</li>
                   <li><strong>Screenshot Carousel Creator:</strong> Select multiple comments and instantly get a downloadable set of images and an AI-generated caption for a high-engagement carousel post.</li>
                </ul>
            `
        },
        presskit: {
            title: "9. Press Kit Generator",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>A comprehensive builder to create a studio-quality, multi-page press kit.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Present your film professionally to journalists, festivals, and sales agents.</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li><strong>Smart Template System:</strong> Choose from genre-optimized templates (Horror, Drama, Festival, etc.) or build your own.</li>
                    <li><strong>AI-Powered Content:</strong> Instantly generate professional cast/crew bios and draft press releases.</li>
                    <li><strong>Full Customization:</strong> Add posters, loglines, synopses, director's statements, and a complete, importable credit list.</li>
                    <li><strong>Professional PDF Export:</strong> Generate a polished, print-ready PDF in multiple formats.</li>
                </ul>
            `
        },
        influencer: {
            title: "10. AI Press Strategy & Influencer Hub",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>An integrated toolkit for your PR and digital outreach efforts.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Reach the right people with the right message.</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li><strong>AI Press Strategy:</strong> Generate a comprehensive PR plan, including strategy pillars, target outlet tiers, a press timeline, and key talking points.</li>
                    <li><strong>AI Influencer Discovery:</strong> Uncover a list of real-world influencers on TikTok, YouTube, and Instagram who are a perfect fit for your film, complete with collaboration ideas.</li>
                    <li><strong>Contact & Status Tracking:</strong> Add discovered influencers to your contact list and track their status from "To Contact" to "Confirmed."</li>
                </ul>
            `
        },
        campaign: {
            title: "11. Campaign Manager",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>A central dashboard to organize all the ad campaigns you create from the Ad Concept Studio and Remix Studio.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>Keep your entire digital advertising plan in one place. Track campaign status (Draft, Live, Completed), manage creative assets, and prepare for launch.</p>
            `
        },
        dashboard: {
            title: "12. Performance Dashboard",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>A high-level analytics dashboard that visualizes your project's overall health.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>See the big picture at a glance. Track key metrics like Total Marketing Budget vs. Domestic Forecast, ROAS (Return on Ad Spend), Predicted Buzz Factor, and Marketing Mix Score. An AI Executive Summary provides a constant, plain-language assessment of your strategy's strengths and weaknesses.</p>
            `
        },
        dossier: {
            title: "13. The Professional Dossier",
            content: `
                <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                <p>The culmination of all your work. This feature automatically synthesizes every piece of data and analysis from across the platform into a single, stunning, multi-page presentation.</p>
                <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                <p>This is your ultimate pitch tool.</p>
                <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li><strong>Polished & Professional:</strong> Beautifully designed sections for Audience DNA, Visual Strategy, Trailer Analysis, Financials, and more.</li>
                    <li><strong>Shareable & Secure:</strong> Generate a secure, read-only web link to share with investors, sales agents, or distributors.</li>
                    <li><strong>Custom PDF Export:</strong> Export your dossier as a studio-quality PDF, choosing from multiple professional templates (Investor, Creative, Festival, etc.) to tailor the presentation to your audience.</li>
                </ul>
            `
        },
        blueprint: {
            title: "14. The Marketing Blueprint",
            content: `
                 <h4 class="font-semibold text-purple-400">Core Functionality:</h4>
                 <p>Synthesize all data from Audience Genius, Campaign Intelligence, and your Budget Plan into a polished, multi-page strategic document. This isn't just a report; it's your playbook for success.</p>
                 <h4 class="font-semibold text-purple-400 mt-4">The Filmmaker's Edge:</h4>
                 <p>Move from analysis to action. The blueprint provides a clear, step-by-step guide your team can implement today.</p>
                 <ul class="list-disc list-inside mt-2 space-y-1 text-sm">
                     <li><strong>Strategic Analysis:</strong> Each section begins with a concise, executive-level paragraph analyzing what the data means for this specific part of your campaign.</li>
                     <li><strong>Action Items:</strong> Following the analysis, you get a bulleted list of 3-5 concrete, actionable directives. No fluff, just clear instructions.</li>
                     <li><strong>Comprehensive Scope:</strong> Covers everything from Executive Summary and Target Audience Deep Dives to Go-to-Market Plans and Digital PR Activation.</li>
                     <li><strong>Exportable & Shareable:</strong> Generate the complete blueprint as a professional PDF, ready to share with your team, stakeholders, and agencies to ensure everyone is aligned and executing on the same data-driven strategy.</li>
                 </ul>
             `
        },
    };

    const openModal = (featureId) => {
        const data = featureData[featureId];
        if (data) {
            setModalData(data);
            document.body.style.overflow = 'hidden';
        }
    };

    const closeModal = () => {
        setModalData(null);
        document.body.style.overflow = '';
    };

    const FeatureCard = ({ id, title, children }) => (
        <div 
            className="feature-card bg-slate-900/50 border border-slate-800 p-6 rounded-xl cursor-pointer"
            onClick={() => openModal(id)}
        >
            <h3 className="font-bold text-xl text-white mb-2">{title}</h3>
            <p className="text-slate-400 text-sm">{children}</p>
        </div>
    );
    
    return (
        <section>
             <nav className="bg-[#0a0a0a]/80 backdrop-blur-lg sticky top-0 z-40 border-b border-slate-800">
                <div className="container mx-auto px-6 py-4 flex justify-center items-center">
                    <div className="flex items-center space-x-8 text-sm font-medium text-slate-400">
                        <a href="#phase-1" className={`nav-link hover:text-purple-400 transition-colors ${activeSection === 'phase-1' ? 'active' : ''}`}>Strategy</a>
                        <a href="#phase-2" className={`nav-link hover:text-purple-400 transition-colors ${activeSection === 'phase-2' ? 'active' : ''}`}>Creative</a>
                        <a href="#phase-3" className={`nav-link hover:text-purple-400 transition-colors ${activeSection === 'phase-3' ? 'active' : ''}`}>Execution</a>
                        <a href="#phase-4" className={`nav-link hover:text-purple-400 transition-colors ${activeSection === 'phase-4' ? 'active' : ''}`}>Reporting</a>
                    </div>
                </div>
            </nav>

            <div id="features" className="container mx-auto px-6 py-16">
                 {/* Phase 1 */}
                 <div id="phase-1" ref={el => sectionsRef.current[0] = el} className="py-12 scroll-mt-20">
                    <div className="text-center mb-12">
                         <span className="text-purple-400 font-semibold">PHASE 1</span>
                         <h2 className="text-4xl font-bold text-white mt-2">Strategy & Forecasting</h2>
                         <p className="text-slate-400 mt-3 max-w-2xl mx-auto">Build your entire marketing plan on a bedrock of data, not assumptions.</p>
                     </div>
                     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard id="audience" title="1. Audience Genius">Define your ideal viewer with deep demographic and psychographic insights to guide your entire campaign.</FeatureCard>
                        <FeatureCard id="intelligence" title="2. Campaign Intelligence Center">Find the marketing gold inside your script by analyzing it for marketable moments and quotable dialogue.</FeatureCard>
                        <FeatureCard id="revenue" title="3. Revenue Forecaster">Project your film's potential earnings with powerful financial modeling for theatrical and streaming releases.</FeatureCard>
                        <FeatureCard id="budget" title="4. Budget Planner">Translate your marketing budget into a detailed, AI-optimized allocation plan for maximum impact.</FeatureCard>
                        <FeatureCard id="competitor" title="5. Competitive Intelligence">Generate a deep-dive analysis of competitor strategies to find your unique market position.</FeatureCard>
                     </div>
                 </div>

                 {/* Phase 2 */}
                 <div id="phase-2" ref={el => sectionsRef.current[1] = el} className="py-12 scroll-mt-20">
                     <div className="text-center mb-12">
                         <span className="text-purple-400 font-semibold">PHASE 2</span>
                         <h2 className="text-4xl font-bold text-white mt-2">Creative Development</h2>
                         <p className="text-slate-400 mt-3 max-w-2xl mx-auto">Transform strategic insights into compelling, audience-ready creative assets.</p>
                     </div>
                     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard id="visual" title="6. Visual A/B Test Lab">Upload two poster versions and let our AI recommend which one will perform best with your target audience.</FeatureCard>
                        <FeatureCard id="adstudio" title="7. Ad Concept Studio">Instantly generate complete A/B test concepts with unique ad copy and AI-generated visuals.</FeatureCard>
                        <FeatureCard id="remix" title="8. Audience Voice Remix Studio">Turn real social media comments into powerful marketing content like carousels and graphic posts.</FeatureCard>
                     </div>
                 </div>

                {/* Phase 3 */}
                <div id="phase-3" ref={el => sectionsRef.current[2] = el} className="py-12 scroll-mt-20">
                    <div className="text-center mb-12">
                        <span className="text-purple-400 font-semibold">PHASE 3</span>
                        <h2 className="text-4xl font-bold text-white mt-2">Execution & Outreach</h2>
                        <p className="text-slate-400 mt-3 max-w-2xl mx-auto">Organize, manage, and deploy your marketing assets with professional-grade tools.</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard id="presskit" title="9. Press Kit Generator">Create a studio-quality, multi-page press kit using smart templates and AI-powered content generation.</FeatureCard>
                        <FeatureCard id="influencer" title="10. AI Press & Influencer Hub">Generate a full PR plan and discover the perfect influencers to champion your film.</FeatureCard>
                        <FeatureCard id="campaign" title="11. Campaign Manager">Organize all your ad campaigns in a central dashboard to track status and manage creative.</FeatureCard>
                    </div>
                </div>

                {/* Phase 4 */}
                <div id="phase-4" ref={el => sectionsRef.current[3] = el} className="py-12 scroll-mt-20">
                    <div className="text-center mb-12">
                        <span className="text-purple-400 font-semibold">PHASE 4</span>
                        <h2 className="text-4xl font-bold text-white mt-2">Reporting & Delivery</h2>
                        <p className="text-slate-400 mt-3 max-w-2xl mx-auto">Synthesize your work into actionable reports and stunning presentations.</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard id="dashboard" title="12. Performance Dashboard">Visualize your project's health with key metrics like ROAS, Predicted Buzz, and an AI Executive Summary.</FeatureCard>
                        <FeatureCard id="dossier" title="13. The Professional Dossier">The ultimate pitch tool. Synthesize all platform data into a stunning, shareable web page or a custom PDF for investors.</FeatureCard>
                        <FeatureCard id="blueprint" title="14. The Marketing Blueprint">Generate a step-by-step strategic plan with actionable intel and concrete implementation steps for your team.</FeatureCard>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {modalData && (
                <div 
                    className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={closeModal}
                >
                    <div 
                        className="modal-content bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-8 max-w-2xl w-full max-h-[80vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-4 flex-shrink-0">
                            <h2 className="text-2xl font-bold text-white">{modalData.title}</h2>
                            <button onClick={closeModal} className="text-slate-400 hover:text-white text-3xl">&times;</button>
                        </div>
                        <div className="overflow-y-auto pr-4">
                            <div className="text-slate-300 space-y-4" dangerouslySetInnerHTML={{ __html: modalData.content }} />
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

// --- MAIN COMPONENT ---

const LandingSite = ({ setNotification, auth }) => {
    const [page, setPage] = useState('home');
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [pendingPlan, setPendingPlan] = useState(null);

    useEffect(() => {
        document.documentElement.classList.add('dark');
        document.body.style.backgroundColor = '#0a0a0a'; 
        return () => {
            document.documentElement.classList.remove('dark');
            document.body.style.backgroundColor = '';
        };
    }, []);

    const navigate = (targetPage) => {
        setPage(targetPage);
        window.location.hash = targetPage;
    };

    const runCheckout = async (user, cycle) => {
        setIsCheckoutLoading(true);
        try {
             const priceId = cycle === 'monthly' 
            ? 'price_1SrREDKHy89O5QxfQQB4HIZA' 
            : 'price_1SrREBKHy89O5QxfRHoErlko';

            const response = await fetch('/api/checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    priceId, 
                    userId: user.uid,
                    userEmail: user.email 
                }),
            });

            const data = await response.json();
            if (data.url) {
                window.location.href = data.url; 
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (error) {
            console.error(error);
            setNotification({ type: 'error', message: 'Checkout failed. Please try again.' });
            setIsCheckoutLoading(false);
        }
    };

    const handleSubscribe = async (cycle) => {
        if (auth.currentUser) {
            runCheckout(auth.currentUser, cycle);
        } else {
            setPendingPlan(cycle);
            const signupSection = document.getElementById('signup');
            if (signupSection) {
                signupSection.scrollIntoView({ behavior: 'smooth' });
            } else {
                navigate('signup');
            }
        }
    };

    const handleSignIn = async (email, password) => {
        try {
            if (!auth) throw new Error("Authentication service is not available.");
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            setNotification({ type: 'error', message: error.message });
        }
    };
    
    // --- UPDATED SIGNUP HANDLER (THE FIX PART 1) ---
    const handleSignUp = async (email, password) => {
        try {
            if (!auth) throw new Error("Authentication service is not available.");
            
            if (pendingPlan) {
                // SET BOTH FLAGS
                sessionStorage.setItem('blackbox_pending_checkout', 'true');
                sessionStorage.setItem('blackbox_pending_plan_cycle', pendingPlan); // <--- ADD THIS
                sessionStorage.setItem('blackbox_checkout_ts', Date.now().toString());
            }
            
            // Create User. This triggers AuthGate.
            // We do NOT need to run checkout here anymore, AuthGate handles it.
            await createUserWithEmailAndPassword(auth, email, password);
            
        } catch (error) {
            sessionStorage.removeItem('blackbox_pending_checkout');
            sessionStorage.removeItem('blackbox_pending_plan_cycle');
            setNotification({ type: 'error', message: error.message });
        }
    };

    const sections = {
        home: (
            <section id="home" className="text-center py-20 md:py-32">
                <h1 className="text-sm md:text-base font-semibold tracking-wide text-purple-400 mb-6">
                    BLACKBOXmi: AI film marketing software for independent filmmakers
                </h1>
                <p className="text-5xl md:text-7xl font-extrabold text-white font-poppins leading-tight">
                    Stop Guessing. <br /> <span className="gradient-text">Start Winning.</span>
                </p>
                <p className="mt-6 max-w-2xl mx-auto text-lg text-zinc-400">
                    BLACKBOXmi is the all-in-one AI suite that transforms your film's marketing from an art into a science. From script to screen, get the data-driven edge you need to succeed.
                </p>
                <div className="mt-10 flex justify-center gap-4">
                    <a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('pricing'); }} className="bg-brand-purple hover:bg-purple-500 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 text-lg glow-effect">
                        Start Free Trial
                    </a>
                    <a href="#features" onClick={(e) => { e.preventDefault(); navigate('features'); }} className="bg-zinc-800 hover:bg-zinc-700 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 text-lg">
                        See How It Works
                    </a>
                </div>
            </section>
        ),
        features: (
            <NewFeaturesSection />
        ),
        pricing: (
            <section id="pricing" className="py-24 relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl bg-purple-600/10 blur-[120px] rounded-full pointer-events-none" />
                
                <div className="relative z-10 text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-bold text-white font-poppins">
                        Simple, Transparent Pricing
                    </h2>
                    <p className="mt-4 text-zinc-400">
                        Everything you need to market your film. No hidden fees.
                    </p>
                    
                    <div className="flex justify-center items-center mt-8 gap-4">
                        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-zinc-500'}`}>Monthly</span>
                        <button 
                            onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                            className="w-14 h-8 bg-zinc-800 rounded-full relative border border-zinc-700 transition-colors hover:border-purple-500"
                        >
                            <div className={`absolute top-1 left-1 w-6 h-6 bg-purple-600 rounded-full transition-transform duration-300 ${billingCycle === 'yearly' ? 'translate-x-6' : ''}`} />
                        </button>
                        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-zinc-500'}`}>
                            Yearly <span className="text-green-400 text-xs ml-1 font-bold">SAVE 20%</span>
                        </span>
                    </div>
                </div>

                <div className="max-w-lg mx-auto relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-purple-600 to-blue-600 rounded-2xl blur opacity-20 transform scale-105"></div>
                    <div className="bg-zinc-900 border border-zinc-700 p-8 rounded-2xl relative shadow-2xl">
                        <div className="text-center border-b border-zinc-800 pb-8">
                    <h3 className="text-2xl font-bold text-white font-poppins">Pro Filmmaker</h3>
                    
                    {/* NEW: TRIAL BADGE */}
                    <div className="inline-block bg-green-500/20 text-green-400 text-xs font-bold px-3 py-1 rounded-full mt-2 mb-1">
                        7-DAY FREE TRIAL
                    </div>

                    <div className="my-6 flex items-baseline justify-center gap-1">
                        <span className="text-5xl font-extrabold text-white">
                            ${billingCycle === 'monthly' ? '199' : '1199'}
                        </span>
                        <span className="text-zinc-400">
                            /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                    </div>
                    
                    <AppButton 
                        onClick={() => handleSubscribe(billingCycle)}
                        disabled={isCheckoutLoading}
                        className="w-full py-4 text-lg shadow-purple-500/25 shadow-lg" 
                        size="lg"
                    >
                        {/* NEW BUTTON TEXT */}
                        {isCheckoutLoading ? 'Redirecting...' : 'Start 7-Day Free Trial'}
                    </AppButton>
                    
                    {/* UPDATE DISCLAIMER */}
                    <p className="text-xs text-zinc-500 mt-3">
                        0 due today. Cancel anytime before trial ends.
                    </p>
                </div>
                        
                        <div className="pt-8">
                            <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">What's Included:</p>
                            <ul className="space-y-4">
                                {[
                                    "7-Day Full Access Pass",
                                    "(25 AI Generation Credits included in Trial)",
                                    "50 AI Generation Credits / mo",
                                    "Audience Genius & Revenue Forecaster",
                                    "Full Press Kit & Dossier Export",
                                    "Campaign Intelligence Center"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-zinc-300">
                                        <div className="bg-green-500/10 p-1 rounded-full">
                                            <Check className="w-4 h-4 text-green-400" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        ),
        signup: (
            <section id="signup" className="py-24">
                <div className="max-w-lg mx-auto">
                    <div className={`bg-brand-gray border p-8 rounded-2xl text-center transition-colors duration-500 ${pendingPlan ? 'border-purple-500 shadow-purple-500/20 shadow-2xl' : 'border-zinc-800'}`}>
                        <h2 className="text-3xl font-bold text-white font-poppins mb-2">
                            {pendingPlan ? 'Finish Your Subscription' : 'Create Your Account'}
                        </h2>
                        <p className="text-zinc-400 mb-8">
                            {pendingPlan 
                                ? `Create an account to secure your ${pendingPlan} plan.` 
                                : 'Join now and unlock the future of film marketing.'}
                        </p>
                        
                        <SignUpForm onSignUp={handleSignUp} />
                        
                        {pendingPlan && (
                             <p className="text-xs text-zinc-500 mt-4">
                                You will be redirected to Stripe secure checkout immediately after signup.
                             </p>
                        )}
                    </div>
                </div>
            </section>
        )
    };

    return (
       <div className="bg-brand-dark text-zinc-300 font-poppins">
         <SignInModal isOpen={isSignInModalOpen} onClose={() => setIsSignInModalOpen(false)} onSignIn={handleSignIn} />
         <style>{`
            .gradient-text { background: linear-gradient(to right, #a855f7, #6366f1); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
            .glow-effect { box-shadow: 0 0 15px rgba(168, 85, 247, 0.5), 0 0 30px rgba(99, 102, 241, 0.3); }
            .nav-link.active { color: #a855f7; font-weight: 600; }
            .modal-content { animation: slide-up 0.3s ease-out; }
            @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            .feature-card { transition: transform 0.2s ease-out, box-shadow 0.2s ease-out, border-color 0.2s ease-out; }
            .feature-card:hover { transform: translateY(-5px); box-shadow: 0 10px 25px -5px rgba(168, 85, 247, 0.2); border-color: #a855f7; }
        `}</style>
            <nav className="bg-brand-dark/80 backdrop-blur-lg sticky top-0 z-50 border-b border-zinc-800">
                <div className="container mx-auto px-6 py-4 flex justify-between items-center">
                    <a href="#home" onClick={(e) => { e.preventDefault(); navigate('home'); }} className="flex items-center gap-2">
                        <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="3" /><line x1="3" y1="12" x2="21" y2="12" /></svg>
                        <span className="text-xl font-bold font-poppins text-white">BLACKBOX<span className="text-purple-500">mi</span></span>
                    </a>
                    <div className="hidden md:flex items-center space-x-8">
                        <NavLink target="features" navigate={navigate}>Features</NavLink>
                        <NavLink target="pricing" navigate={navigate}>Pricing</NavLink>
                        <button onClick={() => setIsSignInModalOpen(true)} className="hover:text-brand-purple transition-colors font-semibold">Sign In</button>
                        <a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('pricing'); }} className="bg-brand-purple hover:bg-purple-500 text-white font-semibold px-5 py-2 rounded-lg transition-all duration-300 glow-effect">Get Started</a>
                    </div>
                    <div className="md:hidden">
                        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white focus:outline-none">
                            {isMobileMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" /></svg>
                            )}
                        </button>
                    </div>
                </div>
                {isMobileMenuOpen && (
                    <div className="md:hidden bg-brand-dark border-t border-zinc-800 animate-fade-in-down">
                        <div className="px-6 pt-2 pb-4 flex flex-col space-y-3">
                            <a href="#features" onClick={(e) => { e.preventDefault(); navigate('features'); setIsMobileMenuOpen(false); }} className="block py-2 text-white font-semibold hover:text-brand-purple">Features</a>
                            <a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('pricing'); setIsMobileMenuOpen(false); }} className="block py-2 text-white font-semibold hover:text-brand-purple">Pricing</a>
                            <button onClick={() => { setIsSignInModalOpen(true); setIsMobileMenuOpen(false); }} className="text-left py-2 text-white font-semibold hover:text-brand-purple">Sign In</button>
                            <a href="#pricing" onClick={(e) => { e.preventDefault(); navigate('pricing'); setIsMobileMenuOpen(false); }} className="block text-center bg-brand-purple hover:bg-purple-500 text-white font-semibold px-5 py-3 rounded-lg transition-all duration-300 glow-effect mt-2">Get Started</a>
                        </div>
                    </div>
                )}
            </nav>
            <main className="container mx-auto px-6">
                {sections[page]}
            </main>
            <footer className="bg-black/30 border-t border-zinc-800 mt-24">
                <div className="container mx-auto px-6 py-8 text-center text-zinc-500">
                    <p>&copy; {new Date().getFullYear()} BLACKBOXmi. All Rights Reserved.</p>
                    <p className="text-sm mt-2">The AI-Powered Edge for Filmmakers.</p>
                    <p className="text-zinc-500 text-sm">
                  Questions? Contact us at{' '}
                  <a href="mailto:support@blackbox-is.com" className="text-green-brand hover:underline">
                    support@blackbox-is.com
                  </a>
                </p>
                </div>
            </footer>
        </div>
    );
};

export default LandingSite;